import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { connectedAppService, type ConnectedAppView } from '../services/connectedApp.service';

export const connectedAppKeys = {
  all: ['connected-apps'] as const,
  list: () => [...connectedAppKeys.all, 'list'] as const,
};

export const useConnectedApps = () =>
  useQuery<ConnectedAppView[], Error>({
    queryKey: connectedAppKeys.list(),
    queryFn: () => connectedAppService.list(),
    staleTime: 30_000,
  });

/**
 * Disconnecting an app never affects the caller's own browser session, so — unlike
 * `useRevokeAllSessions` — there is no "did I just sign myself out?" case to handle. Refetch
 * and toast unconditionally.
 */
export const useRevokeConnectedApp = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => connectedAppService.revoke(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: connectedAppKeys.list() });
      toast.success('App disconnected');
    },
    onError: () => toast.error('Could not disconnect that app'),
  });
};
